import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EMPTY_PATIENT = {
  name: "", rg: "", phone: "", email: "", cpf: "", data_nascimento: "", sexo: "", cep: "", address: "", number: "",
  complement: "", district: "", city: "", uf: "", convenio: "Particular", carteirinha: "", tipo_sanguineo: "",
  alergias: "", comorbidades: "", origem: "", notes: "",
};

const BLOOD = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-", "Não sabe"];
const ORIGINS = ["Indicação de paciente", "Indicação médica", "Instagram", "Facebook", "Google", "Site / Página VeloClin", "Convênio", "Outro"];

const F = ({ label, children, className = "" }) => (
  <div className={className}><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>
);

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <p className="border-b border-slate-100 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</p>
    {children}
  </div>
);

const Pick = ({ value, onChange, options, testid, placeholder = "—" }) => (
  <Select value={value || undefined} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="mt-1.5"><SelectValue placeholder={placeholder} /></SelectTrigger>
    <SelectContent>{options.map((o) => <SelectItem key={o.v ?? o} value={o.v ?? o}>{o.l ?? o}</SelectItem>)}</SelectContent>
  </Select>
);

export const PatientFields = ({ value: p, onChange, plans = [] }) => {
  const set = (k) => (e) => onChange((x) => ({ ...x, [k]: typeof e === "string" ? e : e.target.value }));
  const onCep = async (e) => {
    const v = e.target.value;
    onChange((x) => ({ ...x, cep: v }));
    const d = v.replace(/\D/g, "");
    if (d.length !== 8) return;
    try {
      const r = await (await fetch(`https://viacep.com.br/ws/${d}/json/`)).json();
      if (!r.erro) onChange((x) => ({ ...x, address: r.logradouro || "", district: r.bairro || "", city: r.localidade || "", uf: r.uf || "" }));
    } catch { /* CEP opcional */ }
  };
  const inp = (k, testid, extra = {}) => <Input data-testid={testid} value={p[k] || ""} onChange={set(k)} className="mt-1.5" {...extra} />;
  const planOpts = plans.length ? plans.map((x) => x.name) : ["Particular"];
  return (
    <div className="space-y-5">
      <Section title="Dados pessoais">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <F label="Nome completo *">{inp("name", "med-patient-name-input")}</F>
          <F label="CPF">{inp("cpf", "med-patient-cpf-input", { placeholder: "000.000.000-00" })}</F>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <F label="Nascimento">{inp("data_nascimento", "med-patient-birth-input", { placeholder: "DD/MM/AAAA" })}</F>
          <F label="Sexo / Gênero"><Pick value={p.sexo} onChange={set("sexo")} testid="med-patient-sexo-select" options={[{ v: "F", l: "Feminino" }, { v: "M", l: "Masculino" }, { v: "O", l: "Outro / Não binário" }, { v: "N", l: "Prefere não informar" }]} /></F>
          <F label="RG">{inp("rg", "med-patient-rg-input")}</F>
        </div>
      </Section>
      <Section title="Contato">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <F label="Telefone / WhatsApp">{inp("phone", "med-patient-phone-input", { placeholder: "(11) 90000-0000" })}</F>
          <F label="E-mail">{inp("email", "med-patient-email-input", { type: "email", placeholder: "paciente@email.com" })}</F>
        </div>
      </Section>
      <Section title="Endereço">
        <div className="grid grid-cols-3 gap-3">
          <F label="CEP"><Input data-testid="med-patient-cep-input" value={p.cep || ""} onChange={onCep} inputMode="numeric" placeholder="00000-000" className="mt-1.5" /></F>
          <F label="Logradouro" className="col-span-2">{inp("address", "med-patient-address-input")}</F>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Número">{inp("number", "med-patient-number-input")}</F>
          <F label="Complemento" className="col-span-2">{inp("complement", "med-patient-complement-input")}</F>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <F label="Bairro">{inp("district", "med-patient-district-input")}</F>
          <F label="Cidade">{inp("city", "med-patient-city-input")}</F>
          <F label="UF"><Input data-testid="med-patient-uf-input" value={p.uf || ""} onChange={(e) => onChange((x) => ({ ...x, uf: e.target.value.toUpperCase().slice(0, 2) }))} className="mt-1.5" /></F>
        </div>
      </Section>
      <Section title="Informações médicas">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <F label="Convênio"><Pick value={p.convenio} onChange={set("convenio")} testid="med-patient-convenio-select" options={planOpts} /></F>
          <F label="Nº da carteirinha">{inp("carteirinha", "med-patient-carteirinha-input")}</F>
          <F label="Tipo sanguíneo"><Pick value={p.tipo_sanguineo} onChange={set("tipo_sanguineo")} testid="med-patient-blood-select" options={BLOOD} /></F>
        </div>
        <F label="Alergias conhecidas">{inp("alergias", "med-patient-alergias-input", { placeholder: "Ex.: dipirona, penicilina, látex" })}</F>
        <F label="Comorbidades / observações importantes">
          <textarea data-testid="med-patient-comorbidades-input" value={p.comorbidades || ""} onChange={set("comorbidades")} rows={2} className="mt-1.5 w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200" />
        </F>
      </Section>
      <Section title="Origem">
        <F label="Como conheceu a clínica?"><Pick value={p.origem} onChange={set("origem")} testid="med-patient-origem-select" options={ORIGINS} placeholder="Selecione" /></F>
      </Section>
    </div>
  );
};
